import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authService } from '../../services/authService';
import { emailVerificationService } from '../../services/emailVerificationService';
import { web3Service } from '../../services/web3Service';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';

const SignUp = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [tier, setTier] = useState('free');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });
  const navigate = useNavigate();

  const validatePassword = (pwd) => {
    const validation = {
      length: pwd?.length >= 12,
      uppercase: /[A-Z]/?.test(pwd),
      lowercase: /[a-z]/?.test(pwd),
      number: /[0-9]/?.test(pwd),
      special: /[!@#$%^&*(),.?":{}|<>]/?.test(pwd)
    };
    setPasswordValidation(validation);
    return Object.values(validation)?.every(v => v === true);
  };

  const handlePasswordChange = (e) => {
    const newPassword = e?.target?.value;
    setPassword(newPassword);
    validatePassword(newPassword);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!validatePassword(password)) {
      setError('Password does not meet security requirements');
      return;
    }

    setLoading(true);

    try {
      const { data, error } = await authService?.signUp(email, password, fullName, tier);
      if (error) {
        setError(error?.message);
      } else {
        // Get verification token from user profile
        const { data: profile } = await authService?.getUserProfile();
        
        if (profile) {
          // Send verification email
          await emailVerificationService?.sendVerificationEmail(
            email,
            fullName,
            profile?.emailVerificationToken || ''
          );
        }
        
        // Show success message instead of immediate navigation
        setSignupSuccess(true);
      }
    } catch (err) {
      setError(err?.message || 'An error occurred during sign up');
    } finally {
      setLoading(false);
    }
  };

  // If signup successful, show verification instructions
  if (signupSuccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-card rounded-lg shadow-lg p-8 border border-border">
            <div className="flex items-center justify-center mb-6">
              <div className="bg-green-500 rounded-full p-4">
                <Icon name="CheckCircle" size={48} className="text-white" />
              </div>
            </div>
            
            <h1 className="text-2xl font-bold text-center text-foreground mb-2">
              Account Created Successfully!
            </h1>
            <p className="text-center text-muted-foreground mb-6">
              Welcome to BOLTPay, {fullName}!
            </p>

            <div className="bg-primary/10 border border-primary rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <Icon name="Mail" size={24} className="text-primary flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-foreground mb-2">Verify Your Email</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    We've sent a verification email to:
                  </p>
                  <p className="text-sm font-semibold text-foreground mb-3">
                    {email}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Please check your inbox and click the verification link or enter the 6-digit code to activate your account.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-yellow-500/10 border border-yellow-500 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <Icon name="AlertTriangle" size={20} className="text-yellow-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-foreground font-semibold mb-1">Important:</p>
                  <p className="text-sm text-muted-foreground">
                    You must verify your email before you can log in to your account.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <Button
                fullWidth
                onClick={() => navigate('/email-verification-portal')}
                iconName="Mail"
              >
                Enter Verification Code
              </Button>
              
              <Button
                fullWidth
                variant="outline"
                onClick={() => navigate('/signin')}
                iconName="ArrowLeft"
              >
                Back to Sign In
              </Button>
            </div>

            <div className="mt-6 p-4 bg-muted rounded-lg">
              <div className="flex items-start gap-2">
                <Icon name="Info" size={18} className="text-primary flex-shrink-0 mt-0.5" />
                <div className="text-sm text-muted-foreground">
                  <p className="font-semibold text-foreground mb-1">Didn't receive the email?</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Check your spam or junk folder</li>
                    <li>Make sure the email address is correct</li>
                    <li>Wait a few minutes for delivery</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-card rounded-lg shadow-lg p-8 border border-border">
          <div className="flex items-center justify-center mb-6">
            <div className="bg-primary rounded-full p-3">
              <img src="/assets/images/22458-1768718790784.png" alt="BOLTPay Logo" className="w-8 h-8 object-contain" />
            </div>
          </div>
          
          <h1 className="text-2xl font-bold text-center text-foreground mb-2">
            Create Your Account
          </h1>
          <p className="text-center text-muted-foreground mb-6">
            Join BOLTPay to manage your crypto payments
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              type="text"
              label="Full Name"
              value={fullName}
              onChange={(e) => setFullName(e?.target?.value)}
              placeholder="Enter your full name"
              required
            />

            <Input
              type="email"
              label="Email"
              value={email}
              onChange={(e) => setEmail(e?.target?.value)}
              placeholder="Enter your email"
              required
            />

            <div>
              <Input
                type="password"
                label="Password"
                value={password}
                onChange={handlePasswordChange}
                placeholder="Create a strong password (min 12 characters)"
                required
              />
              
              {password && (
                <div className="mt-2 space-y-1.5 p-3 bg-muted/50 rounded-md border border-border">
                  <p className="text-xs font-semibold text-foreground mb-2">Password Requirements:</p>
                  <div className="flex items-center gap-2">
                    <Icon 
                      name={passwordValidation?.length ? "CheckCircle2" : "Circle"} 
                      size={14} 
                      className={passwordValidation?.length ? "text-green-500" : "text-muted-foreground"}
                    />
                    <span className={`text-xs ${passwordValidation?.length ? 'text-green-500' : 'text-muted-foreground'}`}>
                      At least 12 characters
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon 
                      name={passwordValidation?.uppercase ? "CheckCircle2" : "Circle"} 
                      size={14} 
                      className={passwordValidation?.uppercase ? "text-green-500" : "text-muted-foreground"}
                    />
                    <span className={`text-xs ${passwordValidation?.uppercase ? 'text-green-500' : 'text-muted-foreground'}`}>
                      One uppercase letter (A-Z)
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon 
                      name={passwordValidation?.lowercase ? "CheckCircle2" : "Circle"} 
                      size={14} 
                      className={passwordValidation?.lowercase ? "text-green-500" : "text-muted-foreground"}
                    />
                    <span className={`text-xs ${passwordValidation?.lowercase ? 'text-green-500' : 'text-muted-foreground'}`}>
                      One lowercase letter (a-z)
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon 
                      name={passwordValidation?.number ? "CheckCircle2" : "Circle"} 
                      size={14} 
                      className={passwordValidation?.number ? "text-green-500" : "text-muted-foreground"}
                    />
                    <span className={`text-xs ${passwordValidation?.number ? 'text-green-500' : 'text-muted-foreground'}`}>
                      One number (0-9)
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon 
                      name={passwordValidation?.special ? "CheckCircle2" : "Circle"} 
                      size={14} 
                      className={passwordValidation?.special ? "text-green-500" : "text-muted-foreground"}
                    />
                    <span className={`text-xs ${passwordValidation?.special ? 'text-green-500' : 'text-muted-foreground'}`}>
                      One special character (!@#$%^&*)
                    </span>
                  </div>
                </div>
              )}
            </div>

            <Input
              type="password"
              label="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e?.target?.value)}
              placeholder="Confirm your password"
              required
            />

            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                Select Tier
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setTier('free')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    tier === 'free' ?'border-primary bg-primary/10' :'border-border bg-muted hover:border-muted-foreground'
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <Icon name="User" size={24} className={tier === 'free' ? 'text-primary' : 'text-muted-foreground'} />
                    <span className="font-semibold text-foreground">Free</span>
                    <span className="text-xs text-muted-foreground">$1K limit</span>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setTier('pro')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    tier === 'pro' ?'border-primary bg-primary/10' :'border-border bg-muted hover:border-muted-foreground'
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <Icon name="Crown" size={24} className={tier === 'pro' ? 'text-primary' : 'text-muted-foreground'} />
                    <span className="font-semibold text-foreground">Pro</span>
                    <span className="text-xs text-muted-foreground">$10K limit</span>
                    <span className="text-xs text-yellow-500 font-medium">$11.99/mo</span>
                  </div>
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-error/10 border border-error rounded-md p-3 flex items-start gap-2">
                <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
                <p className="text-sm text-error">{error}</p>
              </div>
            )}

            <Button
              type="submit"
              fullWidth
              loading={loading}
              iconName="UserPlus"
              disabled={loading}
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-card text-muted-foreground">Or sign up with</span>
              </div>
            </div>

            <div className="mt-4 space-y-3">
              <Button
                type="button"
                variant="outline"
                fullWidth
                onClick={async () => {
                  setError('');
                  setLoading(true);
                  try {
                    await authService?.signInWithGoogle();
                  } catch (err) {
                    setError(err?.message || 'Google sign-up failed');
                    setLoading(false);
                  }
                }}
                disabled={loading}
                className="flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>

              <Button
                type="button"
                variant="outline"
                fullWidth
                onClick={async () => {
                  setError('');
                  setLoading(true);
                  try {
                    // Connect Ethereum wallet
                    const { address, walletType, error: connectError } = await web3Service?.connectMetaMask();
                    
                    if (connectError) {
                      throw new Error(connectError?.message);
                    }

                    // Sign authentication message
                    const message = `Sign this message to authenticate with BOLTPay: ${address}`;
                    const { signature, error: signError } = await web3Service?.signMessageEthereum(message);
                    
                    if (signError) {
                      throw new Error(signError?.message);
                    }

                    // Authenticate with Supabase
                    const { data, error: authError } = await authService?.signInWithEthereumWallet(address, signature);
                    
                    if (authError) {
                      throw new Error(authError?.message);
                    }

                    // Redirect to dashboard
                    navigate('/user-wallet-dashboard');
                  } catch (err) {
                    setError(err?.message || 'Ethereum wallet connection failed');
                    setLoading(false);
                  }
                }}
                disabled={loading}
                className="flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" viewBox="0 0 256 417" fill="none">
                  <path d="M127.961 0L125.635 7.88867V285.168L127.961 287.489L255.922 212.32L127.961 312.187L126.635 313.777V406.603L127.961 410.999L256 237.023L127.961 312.187Z" fill="#343434"/>
                  <path d="M127.962 0L0 212.32L127.962 287.489V153.763V0Z" fill="#8C8C8C"/>
                  <path d="M127.961 312.187L126.635 313.777V406.603L127.961 410.999L256 237.023L127.961 312.187Z" fill="#3C3C3B"/>
                  <path d="M127.962 410.999V312.187L0 237.023L127.962 410.999Z" fill="#8C8C8C"/>
                  <path d="M127.961 287.489L255.922 212.32L127.961 153.763V287.489Z" fill="#141414"/>
                  <path d="M0 212.32L127.962 287.489V153.763L0 212.32Z" fill="#393939"/>
                </svg>
                Connect Ethereum Wallet
              </Button>

              <Button
                type="button"
                variant="outline"
                fullWidth
                onClick={async () => {
                  setError('');
                  setLoading(true);
                  try {
                    // Connect Solana wallet
                    const { address, walletType, error: connectError } = await web3Service?.connectSolanaWallet();
                    
                    if (connectError) {
                      throw new Error(connectError?.message);
                    }

                    // Sign authentication message
                    const message = `Sign this message to authenticate with BOLTPay: ${address}`;
                    const { signature, publicKey, error: signError } = await web3Service?.signMessageSolana(message);
                    
                    if (signError) {
                      throw new Error(signError?.message);
                    }

                    // Authenticate with Supabase
                    const { data, error: authError } = await authService?.signInWithSolanaWallet(address, signature, publicKey);
                    
                    if (authError) {
                      throw new Error(authError?.message);
                    }

                    // Redirect to dashboard
                    navigate('/user-wallet-dashboard');
                  } catch (err) {
                    setError(err?.message || 'Solana wallet connection failed');
                    setLoading(false);
                  }
                }}
                disabled={loading}
                className="flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" viewBox="0 0 397.7 311.7" fill="none">
                  <defs>
                    <linearGradient id="solanaGradient" x1="360.88" y1="351.46" x2="141.21" y2="-69.29" gradientUnits="userSpaceOnUse">
                      <stop offset="0" stopColor="#00FFA3"/>
                      <stop offset="1" stopColor="#DC1FFF"/>
                    </linearGradient>
                    <linearGradient id="solanaGradient2" x1="264.83" y1="401.6" x2="45.16" y2="-19.15" gradientUnits="userSpaceOnUse">
                      <stop offset="0" stopColor="#00FFA3"/>
                      <stop offset="1" stopColor="#DC1FFF"/>
                    </linearGradient>
                    <linearGradient id="solanaGradient3" x1="312.55" y1="376.69" x2="92.88" y2="-44.06" gradientUnits="userSpaceOnUse">
                      <stop offset="0" stopColor="#00FFA3"/>
                      <stop offset="1" stopColor="#DC1FFF"/>
                    </linearGradient>
                  </defs>
                  <path d="M64.6 237.9c2.4-2.4 5.7-3.8 9.2-3.8h317.4c5.8 0 8.7 7 4.6 11.1l-62.7 62.7c-2.4 2.4-5.7 3.8-9.2 3.8H6.5c-5.8 0-8.7-7-4.6-11.1l62.7-62.7z" fill="url(#solanaGradient)"/>
                  <path d="M64.6 3.8C67.1 1.4 70.4 0 73.8 0h317.4c5.8 0 8.7 7 4.6 11.1l-62.7 62.7c-2.4 2.4-5.7 3.8-9.2 3.8H6.5c-5.8 0-8.7-7-4.6-11.1L64.6 3.8z" fill="url(#solanaGradient2)"/>
                  <path d="M333.1 120.1c-2.4-2.4-5.7-3.8-9.2-3.8H6.5c-5.8 0-8.7 7-4.6 11.1l62.7 62.7c2.4 2.4 5.7 3.8 9.2 3.8h317.4c5.8 0 8.7-7 4.6-11.1l-62.7-62.7z" fill="url(#solanaGradient3)"/>
                </svg>
                Connect Solana Wallet
              </Button>
            </div>
          </div>

          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              Already have an account?{' '}
              <Link to="/signin" className="text-primary hover:underline font-semibold">
                Sign In
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;